import type { Testimonial, TestimonialTone } from '@/constants/testimonials'

import Image from 'next/image'
import { cn } from '@/lib/utils'
import { DriftCarousel } from '@/components/generals/drift-carousel'
import { Reveal } from '@/components/generals/reveal'
import { SectionHeading } from '@/components/generals/section-heading'
import { TESTIMONIALS } from '@/constants/testimonials'

const tones: Record<TestimonialTone, string> = {
    amber: 'bg-[radial-gradient(130%_90%_at_15%_0%,#f2c57c_0%,#9a6a33_38%,#2b1c0d_75%,#120c06_100%)]',
    violet: 'bg-[radial-gradient(130%_90%_at_85%_0%,#b3b7f5_0%,#4d52a3_40%,#1a1b3f_76%,#0b0b1a_100%)]',
    teal: 'bg-[radial-gradient(130%_90%_at_15%_0%,#9edcd0_0%,#2f7a72_40%,#0f2f2c_76%,#061412_100%)]',
    sage: 'bg-[radial-gradient(130%_90%_at_85%_0%,#d9e7b4_0%,#6f8a53_40%,#25321a_76%,#0e130a_100%)]'
}

const TestimonialCard = ({ item }: { item: Testimonial }) => {
    if (item.kind === 'photo') {
        return (
            <figure className='grain relative flex h-[440px] w-[78vw] max-w-[340px] flex-col overflow-hidden rounded-[30px] bg-[radial-gradient(130%_85%_at_50%_100%,#b4c26c_0%,#667c3d_38%,#2d3a1b_78%,#171f0e_100%)] p-2.5 sm:w-[340px]'>
                <div aria-hidden='true' className='hairline pointer-events-none absolute inset-0 rounded-[inherit]' />

                <div className='relative h-[228px] shrink-0 overflow-hidden rounded-[22px] bg-ink'>
                    <Image
                        src={item.image}
                        alt={`${item.name}, a Verdane member`}
                        fill
                        sizes='320px'
                        draggable={false}
                        className='object-cover contrast-[1.03] saturate-[1.05]'
                        style={{ objectPosition: item.position ?? 'center' }}
                    />
                    <div aria-hidden='true' className='absolute inset-x-0 bottom-0 h-1/3 bg-[linear-gradient(180deg,rgba(0,0,0,0),rgba(20,26,10,0.35))]' />
                </div>

                <div className='relative flex flex-1 flex-col items-center justify-between px-4 pb-4 pt-5 text-center'>
                    <blockquote className='text-[15px] leading-relaxed text-cream/90'>
                        &ldquo;{item.quote}&rdquo;
                    </blockquote>

                    <figcaption className='mt-4 font-serif text-lg italic text-cream'>
                        {item.name}, <span className='text-cream/70'>{item.role}</span>
                    </figcaption>
                </div>
            </figure>
        )
    }

    return (
        <figure className={cn('grain relative flex h-[440px] w-[78vw] max-w-[340px] flex-col items-center justify-center overflow-hidden rounded-[30px] p-8 text-center sm:w-[340px]', tones[item.tone])}>
            <div aria-hidden='true' className='hairline pointer-events-none absolute inset-0 rounded-[inherit]' />

            <span aria-hidden='true' className='relative font-serif text-6xl leading-none text-cream/60'>
                &ldquo;
            </span>

            <blockquote className='relative mt-2 text-xl leading-snug tracking-[-0.02em] text-cream'>
                {item.quote}
            </blockquote>

            <figcaption className='relative mt-7 font-serif text-lg italic text-cream'>
                {item.name}, <span className='text-cream/70'>{item.role}</span>
            </figcaption>
        </figure>
    )
}

export const Testimonials = () => {
    return (
        <section id='voices' aria-label='Member stories about Verdane' className='relative overflow-hidden py-24 sm:py-32'>
            <div className='shell'>
                <SectionHeading
                    eyebrow='Member voices'
                    title='Straight from *our members*'
                    subtitle='Honest notes from people with full, busy lives. Drag, swipe or simply let them float past.'
                />
            </div>

            <Reveal className='mt-16'>
                <DriftCarousel
                    items={TESTIMONIALS}
                    label='Stories from members'
                    getKey={item => item.name}
                    renderItem={item => <TestimonialCard item={item} />}
                />
            </Reveal>
        </section>
    )
}
